import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
const HUB_URL = `${API_BASE}/signalr`;

interface SignalRContextValue {
  connection: HubConnection | null;
  isConnected: boolean;
  connectionState: HubConnectionState | null;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler: (...args: unknown[]) => void) => void;
}

const SignalRContext = createContext<SignalRContextValue>({
  connection: null,
  isConnected: false,
  connectionState: null,
  on: () => {},
  off: () => {},
});

export function SignalRProvider({ children }: { children: ReactNode }) {
  const { getToken, isSignedIn } = useAuth();
  const connectionRef = useRef<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<HubConnectionState | null>(null);

  useEffect(() => {
    if (!isSignedIn) return;

    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => getToken().then((t) => t || ''),
      })
      .withAutomaticReconnect([0, 1000, 5000, 10000, 30000])
      .configureLogging(LogLevel.None)
      .build();

    connectionRef.current = connection;

    connection.onreconnected(() => setConnectionState(connection.state));
    connection.onreconnecting(() => setConnectionState(connection.state));
    connection.onclose(() => setConnectionState(HubConnectionState.Disconnected));

    connection
      .start()
      .then(() => setConnectionState(connection.state))
      .catch((err) => {
        const msg = String(err);
        if (msg.includes('503') || msg.includes('not configured')) {
          console.debug('SignalR not configured — real-time updates disabled');
        } else {
          console.warn('SignalR connection failed:', err);
        }
        setConnectionState(HubConnectionState.Disconnected);
      });

    return () => {
      connection.stop();
      connectionRef.current = null;
      setConnectionState(null);
    };
  }, [isSignedIn, getToken]);

  const on = useCallback((event: string, handler: (...args: unknown[]) => void) => {
    connectionRef.current?.on(event, handler);
  }, []);

  const off = useCallback((event: string, handler: (...args: unknown[]) => void) => {
    connectionRef.current?.off(event, handler);
  }, []);

  return (
    <SignalRContext.Provider
      value={{
        connection: connectionRef.current,
        isConnected: connectionState === HubConnectionState.Connected,
        connectionState,
        on,
        off,
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSignalRContext(): SignalRContextValue {
  return useContext(SignalRContext);
}
